import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import {
    createFormDataFromObject,
    type HttpClientOptions,
} from '@quipex/core/primitives';
import { APP_CONFIG_TOKEN } from '@quipex/core/config';
import type {
    UnifiedAIRequest,
    UnifiedAIResponse,
    IMaintenanceImportItem,
} from './models';

@Injectable({
    providedIn: 'root',
})
export class AIApiService {
    // --- Dependency injection ---
    private readonly http = inject(HttpClient);
    private readonly appConfig = inject(APP_CONFIG_TOKEN);

    // --- Constants ---
    private readonly apiUrl = `${this.appConfig.apiAIBase}/api`;

    // --- Public methods ---

    askAssistant(postModel: UnifiedAIRequest, options?: HttpClientOptions) {
        const url = `${this.apiUrl}/ai/quippy`;
        return this.http.post<UnifiedAIResponse>(url, postModel, options);
    }

    analyzeESMDefectImage(file: File, options?: HttpClientOptions) {
        const formData = createFormDataFromObject({ file });
        const url = `${this.apiUrl}/esm/defect/image-analysis`;
        return this.http.post<unknown>(url, formData, options);
    }

    analyzeServiceReport(file: File, options?: HttpClientOptions) {
        const formData = createFormDataFromObject({ file });
        const url = `${this.apiUrl}/esm/service-report-analysis`;
        return this.http.post<unknown>(url, formData, options);
    }

    importAnnualFireReport(
        file: File,
        options?: HttpClientOptions
    ): Observable<IMaintenanceImportItem> {
        const formData = createFormDataFromObject({ file });
        const url = `${this.apiUrl}/esm/aesmr-extraction`;
        return this.http
            .post<IMaintenanceImportItem>(url, formData, options)
            .pipe(
                map((response) => {
                    return {
                        ...response,
                        measures: response.measures.map((measure) => ({
                            ...measure,
                            matched_master_items:
                                measure.matched_master_items.map((item) => ({
                                    ...item,
                                    identifier: item.identifier.toLowerCase(),
                                })),
                        })),
                    };
                })
            );
    }

    importMaintenanceDocument(
        file: File,
        options?: HttpClientOptions
    ): Observable<IMaintenanceImportItem> {
        const formData = createFormDataFromObject({ file });
        const url = `${this.apiUrl}/esm/oc-extraction`;
        return this.http
            .post<IMaintenanceImportItem>(url, formData, options)
            .pipe(
                map((response) => {
                    return {
                        ...response,
                        measures: response.measures.map((measure) => ({
                            ...measure,
                            matched_master_items:
                                measure.matched_master_items.map((item) => ({
                                    ...item,
                                    identifier: item.identifier.toLowerCase(),
                                })),
                        })),
                    };
                })
            );
    }
}
